import cx from 'classnames';
import { pageTitleClass } from '@app/constants/styles';
import { Button } from '@ui/components/ui/button';
import { Bell } from 'lucide-react';
import { SprintToggler, FilterField } from '@app/components/core';
import { TabsType } from '@app/utils/types';
import { MembershipStatus, ProductEntity, UserEntity } from '@data';
import React, { Dispatch, ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { MultiSelectField } from '@ui/components/ui/multi-select';
import { cn } from '@ui/lib/utils';

export interface SprintHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  activeTab: TabsType;
  setActiveTab: Dispatch<TabsType>;
  productIds: string[];
  setProductIds: Dispatch<string[]>;
  products?: { items: ProductEntity[]; count: number };
  handleSendMembershipReminder: () => void;
  user?: UserEntity;
  sendMembershipReminderIsLoading: boolean;
  /** Optional From/To date filter, placed beside the products picker (shown only on the past tab). */
  dateFilter?: ReactNode;
}

const SprintHeader = ({
  activeTab,
  setActiveTab,
  productIds,
  setProductIds,
  products,
  handleSendMembershipReminder,
  user,
  sendMembershipReminderIsLoading,
  dateFilter,
}: SprintHeaderProps): ReactElement => {
  const { t } = useTranslation();

  const productsOptions = products?.items?.map(product => ({ value: product.id, label: product.name })) || [];
  const memberName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  // The members list leads here for departed colleagues too, so the page says who it is looking at and greys
  // out the ping: there is nobody left to nudge about the gaps in this sprint.
  const isDeactivated = user?.status === MembershipStatus.deactivated;

  return (
    <div className="w-full">
      <div className="mb-6">
        <div className="flex w-full min-w-0 flex-wrap items-center justify-between gap-4">
          <h1 className={cx('m-0 flex flex-wrap items-center gap-3', pageTitleClass)}>
            {/* Same shape as a product sprint's title: what the page is, then who it's about in a lighter
                weight beside it. */}
            <span>
              {t('titles.reporting')}{' '}
              <span className="text-xl font-normal text-muted-foreground sm:text-2xl">{memberName}</span>
            </span>
            {/* The outlined pill a sprint product's status uses, in grey: this is a state of the member, not
                a step they've reached. */}
            {isDeactivated && (
              <span className="shrink-0 rounded border border-muted-foreground/50 bg-card px-2 py-[3px] text-xs font-normal leading-none text-muted-foreground">
                {t('tabs.deactivated')}
              </span>
            )}
          </h1>

          {/* One person's page is their sprints, never the organisation-wide Overview. */}
          <SprintToggler activeTab={activeTab} setActiveTab={setActiveTab} showOverview={false} />
        </div>

        <div
          className={cn(
            'mt-4 flex w-full min-w-0 max-w-full flex-col flex-wrap items-stretch justify-between gap-2',
            'sm:flex-row sm:items-end',
            '[&_.inputField>div]:!bg-card',
          )}
        >
          <div className="flex w-full min-w-0 flex-col gap-2 sm:w-auto sm:flex-row sm:items-end sm:gap-4">
            <FilterField>
              <MultiSelectField
                label={t('titles.products')}
                options={productsOptions}
                value={productIds}
                onChange={setProductIds}
                emptyLabel={t('allProducts')}
                searchPlaceholder={t('search')}
                emptySearchLabel={t('noMatches')}
                renderCountSummary={count => t('nItemsSelected', { count })}
              />
            </FilterField>
            {dateFilter}
          </div>
          {/* Title on the wrapper: a disabled button takes no pointer events, so it can't carry one itself. */}
          <span className="max-sm:w-full sm:ml-4" title={isDeactivated ? t('pingNeedsActiveMember') : undefined}>
            <Button
              className="max-sm:w-full"
              size="sm"
              disabled={isDeactivated}
              loading={sendMembershipReminderIsLoading}
              onClick={handleSendMembershipReminder}
            >
              <Bell />
              {t('ping')}
            </Button>
          </span>
        </div>
      </div>
    </div>
  );
};
export default SprintHeader;
